import { useEffect, useRef, useState } from "react";
import { APIProvider, useMapsLibrary } from "@vis.gl/react-google-maps";
import { MapPin, Loader2 } from "lucide-react";

/**
 * AU-only street address autocomplete backed by Google Places (New).
 *
 * Renders a plain text input (styled to match the existing signup wizard)
 * with a suggestion dropdown. Restricted to ``country: 'au'`` and biased
 * towards the ANZ region so the top hit is almost always the correct
 * address on the first two typed chars.
 *
 * On selection, calls ``onSelect(components)`` with the structured
 * components — {formatted, street_number, route, suburb, state, postcode,
 * country, lat, lng} — so downstream (backend / PDF interpolation) can
 * use the parts without re-parsing a free-text string.
 */
export function AddressAutocomplete({ value, onChange, onSelect,
                                       label = "Service address",
                                       required = false,
                                       testid = "address-autocomplete",
                                       placeholder = "Start typing your address…" }) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    // Fall back to a plain input if the key isn't configured yet — never
    // block the wizard.
    return (
      <PlainAddressInput value={value} onChange={onChange} label={label}
                          required={required} testid={testid}
                          placeholder="123 Example St, Suburb, State, 0000" />
    );
  }
  return (
    <APIProvider apiKey={apiKey} libraries={["places"]}>
      <PlacesAutocompleteInner value={value} onChange={onChange} onSelect={onSelect}
                                label={label} required={required} testid={testid}
                                placeholder={placeholder} />
    </APIProvider>
  );
}

function PlainAddressInput({ value, onChange, label, required, testid, placeholder }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <input
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 text-sm"
      />
    </div>
  );
}

function PlacesAutocompleteInner({ value, onChange, onSelect, label, required, testid, placeholder }) {
  const places = useMapsLibrary("places");
  const [predictions, setPredictions] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(null);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Fresh session token per user-interaction session (per Google's
  // billing model — one token from first keystroke to place selection).
  useEffect(() => {
    if (places && !sessionRef.current) {
      sessionRef.current = new places.AutocompleteSessionToken();
    }
  }, [places]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function onDoc(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  function onInputChange(v) {
    onChange(v);
    if (!places || !v || v.length < 3) {
      setPredictions([]); setOpen(false); return;
    }
    setBusy(true); setOpen(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: v,
          sessionToken: sessionRef.current,
          includedRegionCodes: ["au"],
          // Bias toward street/postal addresses (skip generic POIs)
          includedPrimaryTypes: ["street_address", "premise", "subpremise", "postal_code"],
          language: "en-AU",
          region: "au",
        });
        setPredictions((suggestions || []).slice(0, 6));
      } catch (err) {
        console.warn("Places autocomplete error:", err);
        setPredictions([]);
      } finally { setBusy(false); }
    }, 200);
  }

  async function selectPrediction(sug) {
    if (!sug?.placePrediction) return;
    try {
      const place = sug.placePrediction.toPlace();
      await place.fetchFields({
        fields: ["addressComponents", "formattedAddress", "location"],
        sessionToken: sessionRef.current,
      });
      const comps = {};
      for (const c of place.addressComponents || []) {
        const t = c.types || [];
        if (t.includes("street_number")) comps.street_number = c.shortText || c.longText;
        else if (t.includes("route")) comps.route = c.longText;
        else if (t.includes("locality") || t.includes("postal_town")) comps.suburb = c.longText;
        else if (t.includes("administrative_area_level_1")) comps.state = c.shortText;
        else if (t.includes("postal_code")) comps.postcode = c.longText;
        else if (t.includes("country")) comps.country = c.shortText;
      }
      const structured = {
        formatted: place.formattedAddress || sug.placePrediction.text?.text || "",
        ...comps,
        lat: place.location?.lat(), lng: place.location?.lng(),
      };
      onChange(structured.formatted);
      onSelect?.(structured);
    } catch (err) {
      console.warn("Places fetch fields failed:", err);
      onChange(sug.placePrediction.text?.text || "");
    } finally {
      setOpen(false);
      // Rotate session token after a successful pick (Google billing).
      if (places) sessionRef.current = new places.AutocompleteSessionToken();
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
        <input
          data-testid={testid}
          value={value || ""}
          onChange={(e) => onInputChange(e.target.value)}
          onFocus={() => value && value.length >= 3 && predictions.length > 0 && setOpen(true)}
          placeholder={placeholder}
          autoComplete="off"
          className="w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 pl-10 pr-10 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 text-sm"
        />
        {busy && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 animate-spin" />}
      </div>
      {open && predictions.length > 0 && (
        <div
          data-testid="address-predictions"
          className="absolute z-30 left-0 right-0 mt-1 rounded-lg border border-slate-800 bg-slate-950 shadow-xl overflow-hidden"
        >
          {predictions.map((sug, i) => {
            const pp = sug.placePrediction;
            const main = pp?.mainText?.text || pp?.text?.text || "(no name)";
            const secondary = pp?.secondaryText?.text || "";
            return (
              <button
                type="button"
                key={pp?.placeId || i}
                data-testid={`address-prediction-${i}`}
                onClick={() => selectPrediction(sug)}
                className="w-full text-left px-3 py-2.5 hover:bg-slate-900 border-b border-slate-800/70 last:border-b-0 transition-colors"
              >
                <div className="text-sm text-slate-100 truncate">{main}</div>
                {secondary && (
                  <div className="text-[11px] text-slate-500 truncate">{secondary}</div>
                )}
              </button>
            );
          })}
        </div>
      )}
      <div className="mt-1 text-[10px] text-slate-500 flex items-center gap-1">
        Powered by Google · Australia only
      </div>
    </div>
  );
}
